import structure from 'new/routes/structure';
import { SectionKey } from 'new/state/sectionNavigation/types';

const SECTION_NAME_TO_KEY: Record<string, SectionKey> = {
  Integrate: 'integrate',
  AI: 'ai',
  Tasks: 'tasks',
  Discover: 'discover',
  Monitor: 'monitor',
  Settings: 'settings',
  Collaborate: 'collaborate',
  Marketplace: 'marketplace',
};

export function getPathnameFromFullPath(fullPath: string): string {
  return fullPath.split('?')[0].split('#')[0];
}

/**
 * Section-specific path patterns that should be excluded from restoration.
 * These are paths that require proper state/context to function correctly.
 *
 * Each pattern can be:
 * - A string that the path should end with (e.g., '/select-type')
 * - A RegExp for more complex matching
 */
const SECTION_FORM_PATH_PATTERNS: Partial<Record<SectionKey, Array<string | RegExp>>> = {
  integrate: [
    // Flow type selection page
    '/select-type',
    // Credential creation/edit pages
    '/credentials/new',
    // Data map creation/edit pages
    '/tools/lookups/new',
  ],
  // Future sections can be added here:
  // settings: ['/add', '/create'],
  // marketplace: ['/request-access'],
};

/**
 * Checks if a pathname is a form/creation/edit page that should not be
 * restored when navigating back to a section.
 *
 * Form pages require proper state/context to function correctly and
 * should not be landed on directly via section navigation restoration.
 *
 * Global patterns matched:
 * - Paths ending with /new (e.g., /integrate/sources/new)
 * - Paths containing /edit (e.g., /integrate/sources/123/edit)
 * - Paths containing /send (e.g., /integrate/nexsets/123/send)
 * - Flow creation paths (e.g., /integrate/flows/cdc/source/new)
 *
 * Section-specific patterns are also checked based on the section parameter.
 *
 * @param pathname - The pathname to check
 * @param section - Optional section key to check section-specific patterns
 */
export function isFormPath(pathname: string, section?: SectionKey): boolean {
  const pathnameOnly = getPathnameFromFullPath(pathname);

  // Global patterns - apply to all sections

  // Check for paths ending with /new
  if (pathnameOnly.endsWith('/new')) {
    return true;
  }

  // Check for paths containing /edit (typically at the end like /123/edit)
  if (/\/\d+\/edit\/?$/.test(pathnameOnly) || pathnameOnly.endsWith('/edit')) {
    return true;
  }

  // Check for paths containing /send (like /nexsets/123/send)
  if (/\/\d+\/send\/?$/.test(pathnameOnly) || pathnameOnly.endsWith('/send')) {
    return true;
  }

  // Section-specific patterns
  if (section) {
    const sectionPatterns = SECTION_FORM_PATH_PATTERNS[section];
    if (sectionPatterns) {
      for (const pattern of sectionPatterns) {
        if (typeof pattern === 'string') {
          if (pathnameOnly.endsWith(pattern)) {
            return true;
          }
        } else if (pattern instanceof RegExp) {
          if (pattern.test(pathnameOnly)) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

export function getSectionKeyFromPath(pathname: string): SectionKey | null {
  const pathnameOnly = getPathnameFromFullPath(pathname);

  for (const section of structure) {
    if (section.name === 'Overview') continue;

    if (pathnameOnly.startsWith(section.to)) {
      const sectionKey = SECTION_NAME_TO_KEY[section.name];
      if (sectionKey) {
        return sectionKey;
      }
    }
  }

  return null;
}
